import { estimateShipEta } from './eta.js';
import { SCHEDULE } from '../config/routeStatus.js';

// Contract-deadline schedule status for a ship's current voyage (#53-T8).
// Pure and EventBus/GameState-free so the server can import it.

const HOUR_MS = 3600000;

function _toMs(value) {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function _contractList(contracts) {
  if (!contracts) return [];
  if (contracts instanceof Map) return [...contracts.values()];
  return Array.isArray(contracts) ? contracts : [...contracts];
}

/** Destination port id of the voyage: sailing leg, else the held departure. */
function _voyageDestination(ship) {
  if (ship.status === 'sailing' && ship.currentRoute?.destinationPortId) {
    return ship.currentRoute.destinationPortId;
  }
  return ship.pendingDeparture?.portId || ship.currentRoute?.destinationPortId || null;
}

/**
 * Earliest deadline among cargo that belongs to an active (`accepted`)
 * contract and is bound for the voyage destination. Failed/expired
 * contracts leave their Cargo on board, so contracts are checked.
 *
 * @param {Ship} ship
 * @param {Map|Array|Iterable} contracts  contracts (any status; filtered here)
 * @returns {Date|null}
 */
export function getVoyageDeadline(ship, contracts) {
  if (!ship || !Array.isArray(ship.cargo) || ship.cargo.length === 0) return null;
  const destId = _voyageDestination(ship);
  if (!destId) return null;
  const active = new Set();
  for (const c of _contractList(contracts)) {
    if (c && c.status === 'accepted') active.add(c.id);
  }
  let best = null;
  for (const cargo of ship.cargo) {
    if (!cargo.contractId || !active.has(cargo.contractId)) continue;
    if (cargo.destinationPort !== destId) continue;
    const ms = _toMs(cargo.deadline);
    if (ms !== null && (best === null || ms < best)) best = ms;
  }
  return best === null ? null : new Date(best);
}

/**
 * Classify an ETA against a deadline.
 *
 * @param {Date|null} deadline
 * @param {object} eta        estimateShipEta result
 * @param {Date|null} [bestCaseEta]  used only when `eta` is blocked
 * @returns {{ status:'none'|'onTime'|'behind'|'late'|'unknown', slackHours:number|null }}
 */
export function deriveScheduleStatus(deadline, eta, bestCaseEta = null) {
  const deadlineMs = _toMs(deadline);
  if (deadlineMs === null) return { status: 'none', slackHours: null };

  if (!eta || eta.blockedReason || !eta.etaDate) {
    const bestMs = _toMs(bestCaseEta);
    if (bestMs !== null && bestMs > deadlineMs) {
      return { status: 'late', slackHours: (deadlineMs - bestMs) / HOUR_MS };
    }
    return { status: 'unknown', slackHours: null };
  }

  const slackHours = (deadlineMs - eta.etaDate.getTime()) / HOUR_MS;
  if (slackHours < 0) return { status: 'late', slackHours };
  const threshold = Math.max(
    SCHEDULE.BEHIND_MIN_SLACK_HOURS,
    SCHEDULE.BEHIND_SLACK_RATIO * ((eta.holdHours || 0) + (eta.travelHours || 0))
  );
  return { status: slackHours < threshold ? 'behind' : 'onTime', slackHours };
}

/** Best case for a blocked ETA: depart now over the leg distance at current speed. */
function _bestCaseEta(ship, now) {
  let distance;
  if (ship.status === 'sailing' && ship.currentRoute) {
    distance = ship.currentRoute.distance * (1 - (ship.voyageProgress || 0));
  } else {
    distance = ship.getPendingRoute?.()?.distanceNm;
  }
  const speed = ship.getCurrentSpeed();
  if (!(distance > 0) || !(speed > 0)) return null;
  return new Date(now.getTime() + (distance / speed) * HOUR_MS);
}

/**
 * Schedule status for a ship's current voyage.
 *
 * @param {Ship} ship
 * @param {object} opts  { now:Date, contracts, tradeRoutes? }
 * @returns {{ status:string, deadline:Date|null, etaDate:Date|null,
 *             slackHours:number|null, blockedReason:string|null,
 *             eta:object|null }}
 */
export function estimateShipSchedule(ship, opts = {}) {
  const { now, contracts, tradeRoutes } = opts;
  const deadline = ship ? getVoyageDeadline(ship, contracts) : null;
  if (!deadline) {
    return { status: 'none', deadline: null, etaDate: null, slackHours: null, blockedReason: null, eta: null };
  }
  const eta = estimateShipEta(ship, { now, tradeRoutes });
  const bestCase = eta.blockedReason && now instanceof Date ? _bestCaseEta(ship, now) : null;
  const { status, slackHours } = deriveScheduleStatus(deadline, eta, bestCase);
  return {
    status,
    deadline,
    etaDate: eta.etaDate || null,
    slackHours,
    blockedReason: eta.blockedReason || null,
    eta
  };
}
